import React, { TextareaHTMLAttributes, forwardRef } from 'react';
import { cn } from 'utils/helpers';
import { useLicenseGate } from './LicenseGate';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  rows?: number;
  disableOnLicenseExpired?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helperText,
      fullWidth = false,
      className,
      id,
      rows = 4,
      disabled,
      disableOnLicenseExpired = true,
      ...props
    },
    ref
  ) => {
    const { shouldDisable } = useLicenseGate();
    const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={textareaId}
            className="text-xs font-medium text-secondary-600"
          >
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <textarea
          ref={ref}
          id={textareaId}
          rows={rows}
          disabled={disabled || (disableOnLicenseExpired && shouldDisable)}
          className={cn(
            'w-full px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg border transition-all duration-200',
            'text-base sm:text-sm text-secondary-900 placeholder:text-secondary-400',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
            'disabled:bg-secondary-50 disabled:cursor-not-allowed',
            'resize-vertical min-h-[100px] sm:min-h-[80px]',
            error
              ? 'border-red-500 focus:ring-red-500'
              : 'border-secondary-300 hover:border-secondary-400',
            className
          )}
          {...props}
        />

        {(error || helperText) && (
          <p
            className={cn(
              'text-sm',
              error ? 'text-red-500' : 'text-secondary-500'
            )}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
